import React, { useState, useEffect } from 'react';
import api from '../services/api';
import s from './RevisaoFiscalNF.module.css';

/* ── Helpers ── */
const cel = (esp, atual) => {
  if (!esp) return <span className={s.semConv}>—</span>;
  const igual = esp === atual;
  return (
    <span className={igual ? s.igual : s.diferente}>
      {esp}
      {!igual && atual && <span className={s.atualOld}> ← {atual || 'vazio'}</span>}
      {!igual && !atual && <span className={s.atualOld}> ← vazio</span>}
    </span>
  );
};

export default function RevisaoFiscalNF({ entradaId, numeroNF, onClose }) {
  const [tiposMovimento, setTiposMovimento] = useState([]);
  const [tipoId, setTipoId]                = useState('');
  const [analisando, setAnalisando]        = useState(false);
  const [aplicando, setAplicando]          = useState(false);
  const [resultado, setResultado]          = useState(null);
  const [selecionados, setSelecionados]    = useState(new Set());
  const [erro, setErro]                    = useState('');
  const [msg, setMsg]                      = useState('');

  useEffect(() => {
    api.get('/fiscal/tipos-movimento')
      .then(r => {
        const entradas = (r.data || []).filter(t => t.tipo_operacao === 'E');
        setTiposMovimento(entradas);
        if (entradas.length === 1) setTipoId(String(entradas[0].id));
      })
      .catch(() => {});
  }, []);

  const analisar = async () => {
    if (!tipoId) { setErro('Selecione o tipo de movimento fiscal.'); return; }
    setAnalisando(true); setErro(''); setResultado(null); setMsg('');
    try {
      const { data } = await api.get(`/entradas-nf/${entradaId}/revisao-fiscal`, {
        params: { tipo_movimento_fiscal_id: tipoId },
      });
      setResultado(data);
      const comDif = new Set(data.itens.filter(i => i.tem_diferenca).map(i => i.produto_id));
      setSelecionados(comDif);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao analisar.');
    } finally { setAnalisando(false); }
  };

  const toggleSel = pid => setSelecionados(s => {
    const n = new Set(s);
    n.has(pid) ? n.delete(pid) : n.add(pid);
    return n;
  });

  const selTodos   = () => setSelecionados(new Set((resultado?.itens || []).map(i => i.produto_id)));
  const desTodos   = () => setSelecionados(new Set());
  const selDiverg  = () => setSelecionados(new Set((resultado?.itens || []).filter(i => i.tem_diferenca).map(i => i.produto_id)));

  const aplicar = async () => {
    if (selecionados.size === 0) { setErro('Selecione ao menos um produto.'); return; }
    setAplicando(true); setErro(''); setMsg('');
    try {
      const { data } = await api.post(`/entradas-nf/${entradaId}/aplicar-fiscal`, {
        tipo_movimento_fiscal_id: tipoId,
        produto_ids: Array.from(selecionados),
      });
      setMsg(`${data.atualizados} produto(s) atualizado(s) com sucesso.`);
      await analisar(); // recarrega para mostrar estado atualizado
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao aplicar.');
    } finally { setAplicando(false); }
  };

  const totalDiverg  = resultado?.total_divergencias ?? 0;
  const semConversao = resultado && (
    resultado.conversoes.cst_icms === 0 &&
    resultado.conversoes.cst_pis  === 0
  );

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <div className={s.header}>
          <span>Revisão Fiscal — NF {numeroNF || entradaId}</span>
          <button className={s.btnFechar} onClick={onClose}>✕</button>
        </div>

        <div className={s.body}>
          {/* Seletor tipo de movimento */}
          <div className={s.selRow}>
            <label className={s.selLabel}>Tipo de Movimento Fiscal</label>
            <select className={s.sel} value={tipoId} onChange={e => { setTipoId(e.target.value); setResultado(null); }}>
              <option value="">— Selecione —</option>
              {tiposMovimento.map(t => (
                <option key={t.id} value={t.id}>{t.nome}</option>
              ))}
            </select>
            <button className={s.btnAnalisar} onClick={analisar} disabled={analisando || !tipoId}>
              {analisando ? 'Analisando...' : 'Analisar'}
            </button>
          </div>

          {erro && <div className={s.erro}>{erro}</div>}
          {msg  && <div className={s.ok}>{msg}</div>}

          {semConversao && (
            <div className={s.aviso}>
              Nenhuma conversão de CST cadastrada para este tipo de movimento. Configure em Tipos de Movimento Fiscal.
            </div>
          )}

          {resultado && !semConversao && (
            <>
              <div className={s.resumo}>
                <span>{resultado.itens.length} produto(s) na NF</span>
                <span className={totalDiverg > 0 ? s.resumoDiverg : s.resumoOk}>
                  {totalDiverg > 0 ? `${totalDiverg} com divergência` : 'Todos em conformidade'}
                </span>
                <span className={s.convInfo}>
                  CST ICMS: {resultado.conversoes.cst_icms} regra(s) &nbsp;|&nbsp;
                  CST PIS/COFINS: {resultado.conversoes.cst_pis} regra(s)
                </span>
              </div>

              <div className={s.selAcoes}>
                <button className={s.btnSel} onClick={selDiverg}>Selecionar divergentes</button>
                <button className={s.btnSel} onClick={selTodos}>Todos</button>
                <button className={s.btnSel} onClick={desTodos}>Nenhum</button>
                <span className={s.qtdSel}>{selecionados.size} selecionado(s)</span>
              </div>

              <div className={s.tabelaWrap}>
                <table className={s.tabela}>
                  <thead>
                    <tr>
                      <th className={s.thCheck}></th>
                      <th>Produto</th>
                      <th className={s.thCod}>CST ICMS XML</th>
                      <th className={s.thCod}>Ent. ICMS Esp./Atual</th>
                      <th className={s.thCod}>Saí. ICMS Esp./Atual</th>
                      <th className={s.thCod}>PIS XML</th>
                      <th className={s.thCod}>Ent. PIS Esp./Atual</th>
                      <th className={s.thCod}>COFINS XML</th>
                      <th className={s.thCod}>Ent. COFINS Esp./Atual</th>
                      <th className={s.thCod}>CFOP XML→Ent.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.itens.length === 0 && (
                      <tr><td colSpan={10} className={s.vazio}>Nenhum produto vinculado nesta NF.</td></tr>
                    )}
                    {resultado.itens.map(it => (
                      <tr key={it.produto_id}
                        className={it.tem_diferenca ? s.rowDiverg : s.rowOk}
                        onClick={() => toggleSel(it.produto_id)}
                        style={{ cursor: 'pointer' }}>
                        <td className={s.tdCheck} onClick={e => e.stopPropagation()}>
                          <input type="checkbox"
                            checked={selecionados.has(it.produto_id)}
                            onChange={() => toggleSel(it.produto_id)} />
                        </td>
                        <td className={s.tdProduto}>
                          <span className={s.prodNome}>{it.produto_nome}</span>
                          <span className={s.prodCod}>#{it.produto_codigo}</span>
                        </td>
                        <td className={s.tdMono}>{it.csosn_xml || '—'}</td>
                        <td>{cel(it.esperado.fe_icms_cst, it.atual.fe_icms_cst)}</td>
                        <td>{cel(it.esperado.fs_icms_cst, it.atual.fs_icms_cst)}</td>
                        <td className={s.tdMono}>{it.cst_pis_xml || '—'}</td>
                        <td>{cel(it.esperado.fe_pis_cst, it.atual.fe_pis_cst)}</td>
                        <td className={s.tdMono}>{it.cst_cofins_xml || '—'}</td>
                        <td>{cel(it.esperado.fe_cofins_cst, it.atual.fe_cofins_cst)}</td>
                        <td className={s.tdCfop}>
                          {it.cfop_xml
                            ? <>{it.cfop_xml}{it.cfop_convertido ? <> → <span className={s.cfopConv}>{it.cfop_convertido}</span></> : ''}</>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className={s.footer}>
          <button className={s.btnFecharFoot} onClick={onClose}>Fechar</button>
          {resultado && !semConversao && (
            <button className={s.btnAplicar}
              onClick={aplicar}
              disabled={aplicando || selecionados.size === 0}>
              {aplicando ? 'Aplicando...' : `Aplicar em ${selecionados.size} produto(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
